import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, FolderOpen, Building2, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JoinPage = () => {
  const { inviteCode: paramCode } = useParams();
  const [step, setStep] = useState(paramCode ? 'validate' : 'code');
  const [inviteCode, setInviteCode] = useState(paramCode || '');
  const [inviteInfo, setInviteInfo] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { joinCompany } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  useEffect(() => {
    if (paramCode) {
      validateCode(paramCode);
    }
  }, [paramCode]);

  const validateCode = async (code) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/invites/validate/${code}`);
      setInviteInfo(response.data);
      setFormData({ ...formData, email: response.data.email });
      setStep('form');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired invite code');
      setStep('code');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (!inviteCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }
    validateCode(inviteCode);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await joinCompany({
        invite_code: inviteInfo.invite_code,
        email: formData.email,
        password: formData.password,
        name: formData.name
      });
      toast.success('Welcome to the team!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join company');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen bg-cover bg-center flex items-center justify-center p-4"
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url('https://images.unsplash.com/photo-1772442198624-4fc4d7281e89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwYmx1ZXByaW50fGVufDB8fHx8MTc3NTIzOTg0NHww&ixlib=rb-4.1.0&q=85')`
      }}
      data-testid="join-page"
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 bg-primary px-6 py-3 border-2 border-black shadow-brutal">
            <FolderOpen className="w-10 h-10 text-black" />
            <h1 className="text-3xl font-black text-black tracking-tight">PUNCHLIST</h1>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white border-2 border-black p-6 sm:p-8 shadow-brutal">
          {/* Back Link */}
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-sm font-bold mb-6 hover:text-primary"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>

          {step === 'code' && (
            <>
              <div className="text-center mb-6">
                <Building2 className="w-16 h-16 mx-auto mb-4 text-primary" />
                <h2 className="text-2xl font-black mb-2">Join a Company</h2>
                <p className="text-secondary text-sm">
                  Enter the invite code you received
                </p>
              </div>

              <form onSubmit={handleCodeSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                    Invite Code
                  </label>
                  <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    required
                    data-testid="invite-code-input"
                    className="w-full h-14 border-2 border-black bg-white text-black text-lg p-4 text-center font-mono tracking-widest uppercase focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    placeholder="ABCD1234"
                    maxLength={8}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="validate-code-btn"
                  className="w-full h-14 bg-primary text-black font-bold uppercase tracking-wide border-2 border-black shadow-brutal hover:bg-primary-hover active:translate-y-1 active:shadow-brutal-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Validating...' : 'Continue'}
                </button>
              </form>
            </>
          )}

          {step === 'validate' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-lg font-bold">Validating invite code...</p>
            </div>
          )}

          {step === 'form' && inviteInfo && (
            <>
              <div className="text-center mb-6">
                <Building2 className="w-16 h-16 mx-auto mb-4 text-primary" />
                <h2 className="text-2xl font-black mb-2">Join {inviteInfo.company_name}</h2>
                <p className="text-secondary text-sm">
                  You've been invited as <span className="font-bold">{inviteInfo.role}</span>
                </p>
              </div>

              <form onSubmit={handleJoin} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="join-name-input"
                    className="w-full h-14 border-2 border-black bg-white text-black text-lg p-4 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled
                    data-testid="join-email-input"
                    className="w-full h-14 border-2 border-black bg-gray-100 text-black text-lg p-4 focus:outline-none"
                  />
                  <p className="text-xs text-secondary mt-1">Email is set by the invitation</p>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wide mb-2">
                    Create Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      data-testid="join-password-input"
                      className="w-full h-14 border-2 border-black bg-white text-black text-lg p-4 pr-14 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      placeholder="Create a password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-secondary hover:text-black"
                    >
                      {showPassword ? <EyeOff className="w-6 h-6" /> : <Eye className="w-6 h-6" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="join-submit-btn"
                  className="w-full h-14 bg-primary text-black font-bold uppercase tracking-wide border-2 border-black shadow-brutal hover:bg-primary-hover active:translate-y-1 active:shadow-brutal-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Joining...' : 'Join Company'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default JoinPage;
